import React, { useCallback, useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFile, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { faCloudArrowUp, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { OverFullWindow } from 'components/UI';
import GenericDeleteModal from 'components/UI/V3/Modals/DeleteModal/GenericDeleteModal';
import { useDispatch, useSelector } from 'react-redux';
import styles from './UploadDocument.module.scss';
import { useApiClientWrapper } from 'hooks';
import { ClientsState, ClientsStatus, listDocumentsThunk } from '@spike/clients-action';
import { RootState } from 'store';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024; // 4MB

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(0)} KB`;
}

export interface UploadDocumentProps {
    onClose: () => void;
    onSave: (files: File[]) => void;
    onExitConfirm: () => void;
    isSaving?: boolean;
    uploadError?: string | null;
    stagedFiles: File[];
    clientId: number;
    onRemoveFile: (index: number) => void;
    onAddFiles: (files: File[]) => void;
    exitConfirmOpen: boolean;
    onExitCancel: () => void;
}

export const UploadDocument: React.FC<UploadDocumentProps> = ({
    onClose,
    onSave,
    onExitConfirm,
    isSaving = false,
    uploadError,
    stagedFiles,
    clientId,
    onRemoveFile,
    onAddFiles,
    exitConfirmOpen,
    onExitCancel
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const clientStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const client = useSelector<RootState, ClientsState | undefined>(state => state.clients);

    const [internalError, setInternalError] = useState<string | null>(null);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    useEffect(() => {
        if (pendingFiles.length === 0) {
            if (isValidating) setIsValidating(false);
            return;
        }

        const currentFile = pendingFiles[0];

        switch (clientStatus) {
            case ClientsStatus.DocumentsListSuccess:
                if (isValidating) {
                    const serverDocs = client?.documents || [];
                    const existsInServer = serverDocs.some(
                        (d: any) => d.filename.toLowerCase() === currentFile.name.toLowerCase()
                    );

                    if (existsInServer) {
                        const errorMsg = `${currentFile.name}: already exists in server.`;
                        setInternalError(prev => (prev ? `${prev}; ${errorMsg}` : errorMsg));
                    } else {
                        onAddFiles([currentFile]);
                    }

                    setPendingFiles(prev => prev.slice(1));
                }
                break;

            case ClientsStatus.Error:
                if (isValidating) {
                    setInternalError(prev =>
                        prev ? `${prev}; Error validating ${currentFile.name}` : `Error validating ${currentFile.name}`
                    );
                    setPendingFiles(prev => prev.slice(1));
                }
                break;

            default:
                if (clientStatus !== ClientsStatus.DocumentsListing) {
                    dispatch(listDocumentsThunk(apiClientWrapper, clientId, currentFile.name, 1, 1));
                }
                break;
        }
    }, [clientStatus, pendingFiles, isValidating, dispatch, clientId, apiClientWrapper, onAddFiles, client?.documents]);

    const handleFiles = useCallback(
        (fileList: FileList | File[]) => {
            const filesArray = Array.from(fileList as any) as File[];
            setInternalError(null);

            const toValidateServer: File[] = [];
            const rejectMsgs: string[] = [];

            filesArray.forEach((f: File) => {
                if (!ACCEPTED_TYPES.includes(f.type)) {
                    rejectMsgs.push(`${f.name}: invalid file type (${f.type})`);
                    return;
                }

                if (f.size > MAX_FILE_SIZE_BYTES) {
                    rejectMsgs.push(`${f.name}: file too large (${formatFileSize(f.size)})`);
                    return;
                }

                const isAlreadyInModal = stagedFiles.some(sf => sf.name.toLowerCase() === f.name.toLowerCase());

                if (isAlreadyInModal) {
                    rejectMsgs.push(`${f.name}: already in upload list`);
                    return;
                }

                toValidateServer.push(f);
            });

            if (rejectMsgs.length > 0) {
                setInternalError(rejectMsgs.join('; '));
            }

            if (toValidateServer.length > 0) {
                setPendingFiles(prev => [...prev, ...toValidateServer]);
                setIsValidating(true);
            }
        },
        [onAddFiles, stagedFiles]
    );

    const currentError = uploadError || internalError;

    return (
        <>
            <OverFullWindow
                title="Adding File"
                onClose={onClose}
                showMobileLogo
                centered
            >
                <div className={styles.container}>
                    <div className={styles.containerFile}>
                        <p className={styles.uploadSectionTitle}>Upload File</p>
                        <Dropzone
                            onDrop={(acceptedFiles: File[], fileRejections: any[]) => {
                                if (fileRejections && fileRejections.length > 0) {
                                    const msgs = fileRejections.map((r: any) => {
                                        const f: File = r.file;
                                        const reasons = (r.errors || [])
                                            .map((e: any) => e.message || e.code)
                                            .join(', ');
                                        return `${f.name}: ${reasons}`;
                                    });
                                    setInternalError(msgs.join('; '));
                                }
                                if (acceptedFiles && acceptedFiles.length > 0) {
                                    handleFiles(acceptedFiles);
                                }
                            }}
                            maxSize={MAX_FILE_SIZE_BYTES}
                            multiple
                            disabled={isValidating}
                        >
                            {({ getRootProps, getInputProps, open }) => (
                                <div
                                    {...getRootProps()}
                                    className={clsx(styles.dropzoneRoot, isValidating && styles.disabled)}
                                >
                                    <input
                                        {...getInputProps()}
                                        aria-label="Select files to upload"
                                    />
                                    <FontAwesomeIcon
                                        icon={faCloudArrowUp}
                                        className={styles.dropzoneIcon}
                                    />
                                    <span className={styles.dropzoneText}>
                                        {isValidating ? 'Validating files...' : 'Select files or drag and drop here'}
                                    </span>
                                    <span className={styles.dropzoneHint}>
                                        JPG, JPEG, PNG or PDF, file size no more than 4MB
                                    </span>
                                    <Button
                                        type="button"
                                        label="Add Files"
                                        variant="primary"
                                        size="small"
                                        leftIcon={faPlus}
                                        className={styles.addFilesButton}
                                        disabled={isValidating}
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            open();
                                        }}
                                    />
                                </div>
                            )}
                        </Dropzone>

                        {currentError && <p className={styles.uploadError}>{currentError}</p>}

                        {stagedFiles.length > 0 && (
                            <>
                                <p className={styles.filesUploadTitle}>Files Upload</p>
                                {stagedFiles.map((file, index) => (
                                    <div
                                        key={`${file.name}-${index}`}
                                        className={styles.fileListItem}
                                    >
                                        <div className={styles.fileListLeft}>
                                            <div className={styles.iconWrapper}>
                                                <FontAwesomeIcon
                                                    icon={faCircleCheck}
                                                    className={styles.fileListCheckIcon}
                                                />
                                            </div>
                                            <div className={styles.fileIconAndMeta}>
                                                <FontAwesomeIcon
                                                    icon={faFile}
                                                    className={styles.fileIcon}
                                                />
                                                <div className={styles.fileMeta}>
                                                    <span className={styles.fileListName}>{file.name}</span>
                                                    <div className={styles.fileListSize}>
                                                        {formatFileSize(file.size)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            label="Delete"
                                            leftIcon={faTrashAlt}
                                            variant="outlined"
                                            size="small"
                                            className={styles.optionDeleteBtn}
                                            onClick={() => onRemoveFile(index)}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                    <div className={styles.footer}>
                        <Button
                            label={isSaving ? 'Saving...' : 'Save'}
                            variant="primary"
                            noColorOnDisabled
                            onClick={() => onSave(stagedFiles)}
                            disabled={stagedFiles.length === 0 || isSaving || isValidating}
                            isLoading={isSaving}
                        />
                    </div>
                </div>
            </OverFullWindow>

            {exitConfirmOpen && (
                <GenericDeleteModal
                    title={<span className={styles.modalTitle}>Exit Without Saving?</span>}
                    body={<p className={styles.modalText}>Your unsaved changes will be lost.</p>}
                    onConfirm={onExitConfirm}
                    onCancel={onExitCancel}
                />
            )}
        </>
    );
};

export default UploadDocument;
